import { useState, useEffect } from "react";
import NewsCard from "../components/NewsCard";
import { newsAPI } from "../services/api";
import ScrollReveal, {
  RevealUp,
  RevealScale,
} from "../components/ScrollReveal";

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("सभी");

  const filters = ["सभी", "आगामी", "संपन्न", "त्योहार", "सामुदायिक"];

  // Sample news data for demonstration
  const sampleNews = [
    {
      _id: "1",
      title: "विवाह पंचमी महोत्सव 2025",
      description:
        "इस वर्ष विवाह पंचमी का भव्य आयोजन फुलहर धाम में किया जाएगा। सीता राम विवाह की पावन स्मृति में यह उत्सव मनाया जाता है।",
      eventDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      location: "फुलहर धाम, मधुबनी",
      category: "त्योहार",
      createdAt: new Date().toISOString(),
      image:
        "https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=500",
    },
    {
      _id: "2",
      title: "रामनवमी उत्सव की तैयारियां",
      description:
        "प्रभु श्री राम के जन्मोत्सव की तैयारियां जोरों पर हैं। भक्तों के लिए विशेष व्यवस्था की जा रही है।",
      eventDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(), // 60 days from now
      location: "फुलहर धाम मंदिर परिसर",
      category: "त्योहार",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      image:
        "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=500",
    },
    {
      _id: "3",
      title: "सामुदायिक भंडारा कार्यक्रम",
      description:
        "गरीब और जरूरतमंद लोगों के लिए निःशुल्क भोजन का आयोजन किया गया। सभी भक्तों का स्वागत है।",
      eventDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
      location: "मंदिर प्रांगण",
      category: "सामुदायिक",
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      image: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=500",
    },
    {
      _id: "4",
      title: "मंदिर विकास कार्य प्रगति पर",
      description:
        "तीर्थ यात्रियों की सुविधा के लिए नए कक्ष और विश्राम गृह का निर्माण कार्य चल रहा है।",
      eventDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days from now
      location: "फुलहर धाम",
      category: "सामुदायिक",
      createdAt: new Date(Date.now() - 259200000).toISOString(),
      image:
        "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=500",
    },
  ];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await newsAPI.getAll();
      const data = response.data;
      setNews(data.news || data || []);
    } catch (error) {
      console.error("Error fetching news:", error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const isUpcoming = (eventDate) => {
    return new Date(eventDate) > new Date();
  };

  const filteredNews = news.filter((item) => {
    if (filter === "सभी") return true;
    if (filter === "आगामी") return isUpcoming(item.eventDate);
    if (filter === "संपन्न") return !isUpcoming(item.eventDate);
    return item.category === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-temple-bg flex items-center justify-center">
        <div className="text-center">
          <div className="loader mb-4"></div>
          <p className="text-temple-text">समाचार लोड हो रहे हैं...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-temple-bg">
      {/* Header with Ram-Sita Background */}
      <section className="py-12 ram-sita-bg text-white">
        <RevealUp className="max-w-5xl mx-auto px-4 sm:px-5 text-center">
          <h1 className="text-3xl md:text-5xl font-serif font-bold mb-4">
            समाचार एवं कार्यक्रम
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
            फुलहर धाम की नवीनतम जानकारी और आगामी कार्यक्रम
          </p>
        </RevealUp>
      </section>

      {/* Filter Section */}
      <section className="py-5 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={`px-6 py-3 rounded-full font-bold transition-all duration-200 ${
                  filter === filterOption
                    ? "bg-temple-primary text-white shadow-lg transform scale-105"
                    : "bg-temple-bg text-temple-text hover:bg-gray-100 border border-gray-200 hover:scale-105"
                }`}
              >
                {filterOption}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          {filteredNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredNews.map((newsItem, index) => (
                <ScrollReveal
                  key={`${newsItem._id}-${filter}`}
                  animation="up"
                  delay={index * 100}
                  triggerOnce={false}
                  className="h-full"
                >
                  <NewsCard news={newsItem} />
                </ScrollReveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 text-gray-400">
                <i className="fas fa-newspaper"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-600 mb-2">
                कोई समाचार उपलब्ध नहीं
              </h3>
              <p className="text-gray-500">
                इस श्रेणी में अभी तक कोई समाचार प्रकाशित नहीं किया गया है।
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events Highlight */}
      <section className="py-10 bg-white">
        <RevealUp className="max-w-5xl mx-auto px-4 sm:px-5">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-serif font-bold text-temple-text mb-3">
              आगामी मुख्य कार्यक्रम
            </h2>
            <div className="w-16 h-1 bg-temple-primary mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {news
              .filter((item) => isUpcoming(item.eventDate))
              .slice(0, 2)
              .map((event, index) => (
                <ScrollReveal
                  key={event._id}
                  animation="scale"
                  delay={index * 200}
                  className="bg-gradient-to-r from-temple-primary to-red-600 text-white rounded-3xl p-8 shadow-xl"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-white/20 text-xs font-bold px-3 py-1 rounded-full">
                      आगामी
                    </span>
                    <span className="text-sm opacity-80">
                      {new Date(event.eventDate).toLocaleDateString("hi-IN")}
                    </span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold mb-4">
                    {event.title}
                  </h3>
                  <p className="text-white/90 mb-4 leading-relaxed">
                    {event.description}
                  </p>
                  <div className="flex items-center text-sm">
                    <span className="mr-2">📍</span>
                    <span>{event.location}</span>
                  </div>
                </ScrollReveal>
              ))}
          </div>
        </RevealUp>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-10 bg-temple-accent text-white">
        <RevealUp className="max-w-3xl mx-auto px-4 sm:px-5 text-center">
          <h3 className="text-2xl font-serif font-bold mb-4">
            समाचार अपडेट पाएं
          </h3>
          <p className="text-gray-200 mb-6">
            फुलहर धाम के नवीनतम समाचार और कार्यक्रमों की जानकारी के लिए सदस्यता
            लें।
          </p>
          <ScrollReveal animation="scale" delay={300}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="आपका ईमेल पता"
                className="flex-1 px-4 py-3 rounded-lg text-temple-text"
              />
              <button className="bg-temple-secondary text-temple-text px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition">
                सदस्यता लें
              </button>
            </div>
          </ScrollReveal>
        </RevealUp>
      </section>

      {/* Contact for Events */}
      <section className="py-10 bg-white">
        <RevealScale className="max-w-3xl mx-auto px-4 sm:px-5 text-center">
          <div className="bg-temple-bg rounded-3xl p-8 border border-temple-secondary/20">
            <div className="text-4xl mb-4 text-temple-primary">
              <i className="fas fa-phone"></i>
            </div>
            <h3 className="text-2xl font-serif font-bold text-temple-text mb-4">
              कार्यक्रम की जानकारी के लिए संपर्क करें
            </h3>
            <p className="text-gray-600 mb-6">
              विशेष कार्यक्रमों और उत्सवों की विस्तृत जानकारी के लिए हमसे संपर्क
              करें।
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center text-temple-text font-bold">
                <span className="mr-2 text-temple-secondary">
                  <i className="fas fa-phone"></i>
                </span>
                <span>+91 9470820521</span>
              </div>
              <div className="flex items-center text-temple-text font-bold">
                <span className="mr-2 text-temple-secondary">
                  <i className="fas fa-envelope"></i>
                </span>
                <span>shreesitaramjimandir@gmail.com</span>
              </div>
            </div>
          </div>
        </RevealScale>
      </section>
    </div>
  );
};

export default News;
